import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom"; // Add this import
import Layout from "../../../components/layout/layout";
import { SearchHeader } from "../../../components/seachHeader/SearchHeader";
import {
  Users,
  Briefcase,
  Trash,
  Pencil,
  X,
  Loader,
  Building2,
  Globe,
  MapPin,
  Receipt,
  Edit,
  NotepadText,
  Search, // Add Search icon
} from "lucide-react";
import {
  updateOrganization,
  updateOrganizationByTaxId,
  subscribeToOrganizationsByPlanType,
  cleanupOrgListeners,
} from "../../../store/slices/orgSlice";
import type { RootState, AppDispatch } from "../../../store";
import csc from "countrycitystatejson";
import { getStateFullName } from "../../..//common/AddressControl/stateNameMapper";
import { getCountryFullName } from "../../../common/AddressControl/countryNameMapper";

interface Organization {
  id: string;

  orgId: string;
  name: string;
  users: { userId: string; role: string; joinedAt: any }[];
  totalSessions?: number;
  totalTeams?: number;
  wallet?: number;
  amount?: number;
  totalEarnings?: number;
  lastActivity?: string;
  url?: string;
  taxId?: string;
  teamName?: string;
  billingAddress?: {
    address_1?: string;
    address_2?: string;
    country?: string;
    state?: string;
    city?: string;
    postcode?: string;
  };
  subscriptionId?: string;
}

const AdminTeams = () => {
  const navigate = useNavigate(); // Add navigation hook
  const [activeTab, setActiveTab] = useState("Customer");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<any>(null);
  const [setupTeamModal, setSetupTeamModal] = useState(false);
  const [viewOrganizationModal, setViewOrganizationModal] = useState(false);
  const [editWalletModal, setEditWalletModal] = useState(false);
  const [creditAmount, setCreditAmount] = useState("");
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null);
  console.log("Selected Organization:", selectedOrganization);
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [formData, setFormData] = useState({
    orgName: "",
    teamName: "",
    orgUrl: "",

    taxId: "",
    addressLine1: "",
    addressLine2: "",
    country: "",
    state: "",
    city: "",
    zipCode: "",
  });
  console.log(formData, "Form Data State");
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<
    string | null
  >(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Country and State management
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);

  const dispatch = useDispatch<AppDispatch>();

  const customerOrgs = useSelector((state: RootState) => state.organization.customerOrganizationsCache);
  const expertOrgs = useSelector((state: RootState) => state.organization.expertOrganizationsCache);
  const currentOrgs = activeTab === "Customer" ? customerOrgs : expertOrgs;
  
  // Initialize countries on component mount
  useEffect(() => {
    try {
      const countryList = csc.getCountries();
      console.log("Country data structure:", countryList);
      // Ensure countryList is an array and has the expected structure
      if (Array.isArray(countryList)) {
        setCountries(countryList);
      } else {
        console.warn("Countries data is not in expected format:", countryList);
        setCountries([]);
      }
    } catch (error) {
      console.error("Error loading countries:", error);
      setCountries([]);
    }
  }, []);
  
  // Set up real-time listener for organizations based on active tab
  useEffect(() => {
    dispatch(subscribeToOrganizationsByPlanType({ planType: "customer", listenerId: "adminTeams-customer" }));
    dispatch(subscribeToOrganizationsByPlanType({ planType: "expert", listenerId: "adminTeams-expert" }));
    
    // Cleanup listeners on unmount
    return () => {
      cleanupOrgListeners("adminTeams-customer");
      cleanupOrgListeners("adminTeams-expert");
    };
  }, [dispatch]);

  const handleTabChange = (tab: string) => {
    // Only proceed if tab is actually changing
    if (tab === activeTab) return;

    // Reset all UI state before changing tab
    setSearchTerm("");
    setFilters(null);
    setSelectedOrganizationId(null);
    setSelectedOrganization(null);
    setCurrentPage(1); // Reset to first page
    
    // Close any open modals
    setSetupTeamModal(false);
    setViewOrganizationModal(false);
    setEditWalletModal(false);
    
    // Change the active tab (this will trigger the useEffect to load new data)
    setActiveTab(tab);
  };
  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };
  const handleFilter = (filterData: any) => {
    setFilters(filterData);
  };
  const openSetupTeamModal = (organization?: Organization) => {
    if (organization) {
      setSelectedOrganization(organization);
      setSelectedOrganizationId(organization.id);
      const formDataToSet = {
        orgName: organization.name || "",
        orgId: organization.orgId || "",
        orgUrl: organization.url || "",
        teamName: organization.teamName || "",
        taxId: organization.taxId || "",
        addressLine1: organization.billingAddress?.address_1 || "",
        addressLine2: organization.billingAddress?.address_2 || "",
        country: organization.billingAddress?.country || "",
        state: organization.billingAddress?.state || "",
        city: organization.billingAddress?.city || "",
        zipCode: organization.billingAddress?.postcode || "",
      };

      setFormData(formDataToSet); // If there's a country selected, load its states
      if (formDataToSet.country) {
        try {
          const selectedCountry = csc.getCountryByShort(formDataToSet.country);
          const statesData = selectedCountry?.states || {};

          let statesList: string[] = [];

          // Handle different data formats from the library
          if (Array.isArray(statesData)) {
            statesList = statesData;
          } else if (typeof statesData === "object" && statesData !== null) {
            statesList = Object.keys(statesData);
          } else {
            console.warn(
              "States data is neither array nor object for existing country:",
              statesData
            );
            statesList = [];
          }

          setStates(statesList);
        } catch (error) {
          console.error("Error loading states for existing country:", error);
          setStates([]);
        }
      }
    } else {
      setSelectedOrganization(null);
      setSelectedOrganizationId(null);
      setFormData({
        orgName: "",
        teamName: "",
        orgUrl: "",

        taxId: "",
        addressLine1: "",
        addressLine2: "",
        country: "",
        state: "",
        city: "",
        zipCode: "",
      });
      setStates([]); // Reset states for new organization
    }
    setSetupTeamModal(true);
  };
  const closeSetupTeamModal = () => {
    setSetupTeamModal(false);
    setSelectedOrganization(null);
    setSelectedOrganizationId(null);
    setFormData({
      orgName: "",
      teamName: "",
      orgUrl: "",
      taxId: "",
      addressLine1: "",
      addressLine2: "",
      country: "",
      state: "",
      city: "",
      zipCode: "",
    });
    setStates([]); // Reset states when closing modal
  };

  const openViewOrganizationModal = (organization: Organization) => {
    setSelectedOrganization(organization);
    setSelectedOrganizationId(organization.id);
    setViewOrganizationModal(true);
  };
  const closeViewOrganizationModal = () => {
    setViewOrganizationModal(false);
    setSelectedOrganization(null);
    setSelectedOrganizationId(null);
  };
  const openEditWalletModal = () => {
    setViewOrganizationModal(false); // Close view modal first
    setEditWalletModal(true);
  };

  const closeEditWalletModal = () => {
    setEditWalletModal(false);
    setCreditAmount("");
    setViewOrganizationModal(true); // Reopen view modal
  };
  const handleAddCredit = () => {
    if (creditAmount) {
      alert(`Adding $${creditAmount} in free credit`);
      setCreditAmount("");
      setEditWalletModal(false);
      setViewOrganizationModal(true); // Return to view modal
    }
  }; // Function to navigate to directory with organization filter
  const handleViewTeamMembers = () => {
    if (selectedOrganization) {
      // Navigate with state instead of URL parameters
      navigate("/admin/directory", {
        state: {
          prefilter: {
            activeTab: activeTab.toLowerCase(), // 'customer' or 'expert'
            // organizationId: selectedOrganization.id,
            organizationId: selectedOrganization.orgId,
            organizationName: selectedOrganization.name,
          },
        },
      });
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target; // Handle country change - update states accordingly
    if (name === "country") {
      try {
        const selectedCountry = csc.getCountryByShort(value);
        console.log("Selected country data:", selectedCountry);
        const statesData = selectedCountry?.states || {};
        console.log("States data before processing:", statesData);

        let statesList: string[] = [];

        // Handle different data formats from the library
        if (Array.isArray(statesData)) {
          // If it's already an array, use it as is
          console.log("States is an array:", statesData);
          statesList = statesData;
        } else if (typeof statesData === "object" && statesData !== null) {
          // If it's an object, extract the keys as state names
          statesList = Object.keys(statesData);
          console.log("Converted object keys to array:", statesList);
        } else {
          // Fallback to empty array
          console.warn("States data is neither array nor object:", statesData);
          statesList = [];
        }

        console.log("Final states array:", statesList);
        setStates(statesList);

        setFormData((prev) => ({
          ...prev,
          [name]: value,
          state: "", // Reset state when country changes
        }));
      } catch (error) {
        console.error("Error getting states for country:", error);
        setStates([]);
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          state: "",
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrganization) return;

    setIsLoadingSave(true);

    try {
      const orgData = {
        name: formData.orgName,
        url: formData.orgUrl || "",
        orgId: selectedOrganization.orgId || "",
        taxId: formData.taxId,
        teamName: formData.teamName,
        billingAddress: {
          address_1: formData.addressLine1,
          address_2: formData.addressLine2,
          country: formData.country,
          state: formData.state,
          city: formData.city,
          postcode: formData.zipCode,
        },
      };

      // Always update by organization id
      const result = await dispatch(
        updateOrganization({
          orgId: selectedOrganization.orgId,
          orgData,
        })
      );

      if (updateOrganization.fulfilled.match(result)) {
        // Success - the real-time listener will automatically update the data
        // No need to manually refetch
        closeSetupTeamModal();
      } else {
        console.error("Error updating organization:", result.payload);
      }
    } catch (error) {
      console.error("Error saving organization:", error);
    } finally {
      setIsLoadingSave(false);
    }
  };
  const handleEdit = (organization: any) => {
    openSetupTeamModal(organization);
  };
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };
  return (
    <Layout>
      <div
        className="p-6 bg-white rounded-lg shadow-sm"
        style={{
          height: "calc(100vh - 112px)",
          overflowY: "hidden",
            overflowX:'hidden'
        }}
      >
        {/* Tab Navigation */}
        <div className="flex justify-between mb-2">
          <div className="flex">
            <button
              className={`py-2 px-8 ${
                activeTab === "Customer"
                  ? "border-b-4 border-[#F15D18] text-[16px] nunito-semibold text-[#F15D18]"
                  : "text-[#A8A8A8] text-[16px] nunito-semibold"
              }`}
              onClick={() => handleTabChange("Customer")}
            >
              {/* <Users className="w-4 h-4 inline-block mr-2" /> */}
              Customer
            </button>
            <button
              className={`py-2 px-8 ${
                activeTab === "Expert"
                  ? "border-b-4 border-[#F15D18] text-[16px] nunito-semibold text-[#F15D18]"
                  : "text-[#A8A8A8] text-[16px] nunito-semibold"
              }`}
              onClick={() => handleTabChange("Expert")}
            >
              {/* <Briefcase className="w-4 h-4 inline-block mr-2" /> */}
              Expert
            </button>
          </div>
        </div>{" "}
        {/* Header Section */}
        <SearchHeader
          key={activeTab} // Force re-render when tab changes to reset internal state
          title={`${activeTab} Teams`}
          onSearch={handleSearch}
          onFilter={handleFilter}
          adminTeams={true}
          activeTab={activeTab}
          organizations={currentOrgs}
        />
        {/* Content Area */}
        <div
          className="bg-white rounded-[12px] border-[1px] border-[#EBEBEB] overflow-hidden"
          style={{
            height: "calc(80vh - 150px)",
            overflowY: "auto",
            overflowX: 'hidden'
          }}
        >
          {activeTab === "Customer" ? (
            <CustomerTeamsTable
              key={`customer-${activeTab}`} // Force re-render when tab changes
              organizations={customerOrgs}
              searchTerm={searchTerm}
              filters={filters}
              onEdit={handleEdit}
              onView={openViewOrganizationModal}
              selectedId={selectedOrganizationId}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
            />
          ) : (
            <ExpertTeamsTable
              key={`expert-${activeTab}`} // Force re-render when tab changes
              organizations={expertOrgs}
              searchTerm={searchTerm}
              filters={filters}
              onEdit={handleEdit}
              onView={openViewOrganizationModal}
              selectedId={selectedOrganizationId}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
            />
          )}
        </div>
        {/* Pagination controls - moved outside the container */}
        <PaginationControls
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          organizations={currentOrgs}
          filters={filters}
          searchTerm={searchTerm}
          activeTab={activeTab}
          onPageChange={handlePageChange}
        />
      </div>
      {/* Edit Team Modal */}
      {setupTeamModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeSetupTeamModal();
            }
          }}
        >
          <div className="bg-white rounded-2xl shadow-lg w-full  max-w-2xl p-4 px-6 relative">
            {" "}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-[24px] text-[#0A0A0A] nunito-semibold">
                Edit Organization
              </h2>{" "}
              <button
                className="text-[#344054] hover:text-gray-700"
                onClick={closeSetupTeamModal}
              >
                <X className="w-[20px] h-[20px]" />
              </button>
            </div>{" "}
            <form onSubmit={handleSubmit}>
              <div className="space-y-3">
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-[16px] nunito-semibold text-[#404040] mb-1">
                        Organization Name
                      </label>
                      <input
                        type="text"
                        name="orgName"
                        placeholder="Enter Name"
                        value={formData.orgName}
                        onChange={handleChange}
                        disabled={isLoadingSave}
                        required
                        className="w-full px-3 py-2 border border-[#D7DEDD] text-[#404040] text-[14px] nunito-normal rounded-[6px] focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>

                    <div>
                      <label className="block text-[16px] nunito-semibold text-[#404040] mb-1">
                        Team Name
                      </label>
                      <input
                        type="text"
                        name="teamName"
                        placeholder="Enter Team Name"
                        value={formData.teamName}
                        onChange={handleChange}
                        disabled={isLoadingSave}
                        required
                        className="w-full px-3 py-2 border border-[#D7DEDD] rounded-[6px] text-[14px] nunito-normal focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-[16px] nunito-semibold text-[#404040] mb-1">
                      Organization URL
                    </label>
                    <input
                      type="text"
                      name="orgUrl"
                      placeholder="Enter URL"
                      value={formData.orgUrl}
                      onChange={handleChange}
                      disabled={isLoadingSave}
                      className="w-full px-3 py-2 border border-[#D7DEDD] rounded-[6px] text-[#404040] nunito-normal text-[14px] focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>{" "}
                  <div className="mb-4">
                    <label className="block text-[16px] nunito-semibold text-[#404040] mb-1">
                      Tax ID (Used for Invoicing)
                    </label>
                    <input
                      type="text"
                      name="taxId"
                      placeholder="Enter Tax ID"
                      value={formData.taxId}
                      onChange={handleChange}
                      disabled={isLoadingSave}
                      className="w-full px-3 py-2 border border-[#D7DEDD] rounded-[6px] text-[#404040] nunito-normal text-[14px] focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-[16px] nunito-semibold text-[#404040] mb-1">
                      Address Line 1
                    </label>
                    <input
                      type="text"
                      name="addressLine1"
                      placeholder="Enter Address"
                      value={formData.addressLine1}
                      onChange={handleChange}
                      disabled={isLoadingSave}
                      className="w-full px-3 py-2 border border-[#D7DEDD] rounded-[6px] text-[#404040] text-[14px] nunito-normal focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-[16px] nunito-semibold text-[#404040] mb-1">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      name="addressLine2"
                      placeholder="Enter Address"
                      value={formData.addressLine2}
                      onChange={handleChange}
                      disabled={isLoadingSave}
                      className="w-full px-3 py-2 border border-[#D7DEDD] rounded-[6px] text-[#404040] text-[14px] nunito-normal focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-[16px] nunito-semibold text-[#404040] mb-1">
                        Country
                      </label>
                      <div className="relative">
                        {" "}
                        <select
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          disabled={isLoadingSave}
                          className="w-full px-3 py-2 border border-[#D9D9D9] rounded-[8px] text-[#404040] text-[14px] nunito-normal appearance-none focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                          <option value="">Select country</option>
                          {Array.isArray(countries) &&
                            countries.map((country) => (
                              <option
                                key={country.shortName || country.code}
                                value={country.shortName || country.code}
                              >
                                {country.name}
                              </option>
                            ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                          <svg
                            className="w-[16px] h-[16px] text-[#696969]"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 9l-7 7-7-7"
                            ></path>
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[16px] nunito-semibold text-[#404040] mb-1">
                        State
                      </label>
                      <div className="relative">
                        {" "}
                        <select
                          name="state"
                          // value={formData.state}
                          value={getStateFullName(
                            formData.country,
                            formData.state
                          )}
                          onChange={handleChange}
                          disabled={
                            !formData.country ||
                            !Array.isArray(states) ||
                            states.length === 0 ||
                            isLoadingSave
                          }
                          className="w-full px-3 py-2 border border-[#D9D9D9] rounded-[8px] text-[#404040] text-[14px] nunito-normal appearance-none focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="">
                            {!formData.country
                              ? "Select country first"
                              : "Select state"}
                          </option>
                          {Array.isArray(states) &&
                            states.map((state, index) => (
                              <option key={index} value={state}>
                                {state}
                              </option>
                            ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                          <svg
                            className="w-[16px] h-[16px] text-[#696969]"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 9l-7 7-7-7"
                            ></path>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[16px] nunito-semibold text-[#404040] mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        name="city"
                        placeholder="Enter City"
                        value={formData.city}
                        onChange={handleChange}
                        disabled={isLoadingSave}
                        className="w-full px-3 py-2 border border-[#D7DEDD] rounded-[6px] text-[#404040] text-[14px] nunito-normal focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>

                    <div>
                      <label className="block text-[16px] nunito-semibold text-[#404040] mb-1">
                        Zip Code
                      </label>
                      <input
                        type="text"
                        name="zipCode"
                        placeholder="Enter Zip code"
                        value={formData.zipCode}
                        onChange={handleChange}
                        disabled={isLoadingSave}
                        className="w-full px-3 py-2 border border-[#D7DEDD] rounded-[6px] text-[#404040] text-[14px] nunito-normal focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>
                </div>{" "}
                <div className="flex justify-center">
                  <button
                    type="submit"
                    disabled={isLoadingSave}
                    className="px-4 py-2 w-[95px] h-[44px] bg-[#F15D18] text-white text-[18px] nunito-semibold rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isLoadingSave ? (
                      <Loader className="animate-spin text-white" size={16} />
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </div>
            </form>{" "}
          </div>
        </div>
      )}
      {/* View Organization Modal */}
      {viewOrganizationModal && selectedOrganization && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeViewOrganizationModal();
            }
          }}
        >
          <div className="  p-4 flex w-[554px] items-center justify-center">
            <div className="w-full bg-white rounded-2xl shadow-lg overflow-hidden">
              {/* Header */}
              <div className="flex justify-end items-center pt-3 px-3 border-gray-100">
                <button
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  onClick={closeViewOrganizationModal}
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>{" "}
              {/* org Info Section */}
              <div className="px-3">
                <div className="flex items-center p-4 bg-white rounded-[16px] border-[1px] border-[#C4C4C4] relative">
                  <div className="mr-3">
                    <Building2 className="w-[80px] h-[80px] stroke-[0.5px] text-[#000000]" />
                  </div>
                  <div className="flex-grow">
                    <div className="flex justify-between items-center">
                      <h2 className="text-[20px] nunito-semibold truncate max-w-40 text-[#0A0A0A]">
                        {selectedOrganization.name || ""}
                      </h2>
                      <div className="flex items-center text-[#404040] text-[12px]">
                        <NotepadText className="w-[16px] h-[16px] mr-1 text-[#7B7B7B]" />
                        <span>
                          Tax ID: {selectedOrganization.taxId || "N/A"}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-[14px] nunito-medium text-[#404040]">
                        Website: {selectedOrganization.url || "N/A"}
                      </p>
                      <p className="text-[14px] nunito-medium  max-w-80 text-[#404040]">
                        Address:{" "}
                        {selectedOrganization.billingAddress
                          ? [
                              selectedOrganization.billingAddress.address_1,
                              selectedOrganization.billingAddress.address_2,
                              selectedOrganization.billingAddress.city,
                              getStateFullName(
                                selectedOrganization.billingAddress?.country ||
                                  "",
                                selectedOrganization.billingAddress?.state || ""
                              ),
                              getCountryFullName(
                                selectedOrganization.billingAddress.country ||
                                  ""
                              )
                            ]
                              .filter(Boolean)
                              .join(", ") + ", " +
                            (selectedOrganization.billingAddress.postcode
                              ? " " +
                                selectedOrganization.billingAddress.postcode
                              : "")
                          : ""}
                      </p>
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-4">
                    <button
                      className="text-[#696969]"
                      onClick={() => {
                        closeViewOrganizationModal();
                        openSetupTeamModal(selectedOrganization);
                      }}
                    >
                      <Pencil className="h-[24px] w-[24px] text-[#696969]" />
                    </button>
                  </div>
                </div>
              </div>
              {/* Stats Grid */}
              <div className="mt-4 px-3">
                <div className="px-3     p-4 rounded-[16px] border-[1px] border-[#C4C4C4]">
                  <div className="grid grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-[#F15D18] mb-1">
                        {selectedOrganization.users
                          ? selectedOrganization.users.length
                          : 0}
                      </div>
                      <div className="text-sm text-gray-600">Total Members</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-[#F15D18] mb-1">
                        {selectedOrganization.totalSessions || 0}
                      </div>
                      <div className="text-sm text-gray-600">
                        Total Sessions
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-[#F15D18] mb-1">
                        $
                        {activeTab === "Expert"
                          ? (selectedOrganization.totalEarnings || 0).toFixed(2)
                          : (selectedOrganization.amount || 0).toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-600">
                        {activeTab === "Expert"
                          ? "Total Earnings"
                          : "Total Amount"}
                      </div>
                    </div>
                  </div>
                  {/* Wallet Details - Only show for Customer tab */}
                  {activeTab === "Customer" && (
                    <div className="p-6 border-b border-gray-100">
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-medium text-gray-900">
                            Total Wallet
                          </span>
                          <span className="text-2xl font-bold text-[#F15D18]">
                            ${(selectedOrganization.wallet || 0).toFixed(2)}
                          </span>
                        </div>

                        <div className="space-y-3"> </div>
                      </div>
                    </div>
                  )}
                </div>{" "}
              </div>
              {/* Action Button */}
              <div className="p-6 flex justify-center">
                <button
                  className="w-[202px] h-[40px] item-center bg-[#F15D18] hover:bg-orange-600 text-[#FFFFFF] nunito-semibold py-2 px-6 rounded-[8px] transition-colors duration-200 text-[16px]"
                  onClick={handleViewTeamMembers}
                >
                  View Team Members
                </button>
              </div>
            </div>{" "}
          </div>
        </div>
      )}{" "}
      {/* Edit Wallet Modal */}
      {editWalletModal && selectedOrganization && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-[60]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeEditWalletModal();
            }
          }}
        >
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-md mx-auto relative p-4">
            {/* Header */}
            <div className="flex items-center justify-between p-6 pb-4">
              <h1 className="text-3xl font-light text-gray-800">Edit Wallet</h1>
              <button
                onClick={closeEditWalletModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={28} />
              </button>
            </div>

            {/* Wallet Info Card */}
            <div className="mx-6 mb-6 bg-gray-50 rounded-2xl p-6">
              {/* Total Amount */}
              <div className="flex items-center justify-between mb-6">
                <span className="text-lg text-gray-600 font-light">
                  Total Wallet
                </span>
                <span className="text-3xl font-light text-orange-500">
                  ${(selectedOrganization.wallet || 0).toFixed(2)}
                </span>
              </div>

              <hr className="border-gray-200 mb-6" />

              {/* Breakdown */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 font-light">Free Credit</span>
                  <span className="text-gray-800 font-light">$100.00</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 font-light">Paid Amount</span>
                  <span className="text-gray-800 font-light">
                    $
                    {Math.max(
                      0,
                      (selectedOrganization.wallet || 0) - 100
                    ).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Add Credit Section */}
            <div className="px-6 pb-8">
              <h2 className="text-xl font-light text-gray-800 mb-4">
                Add Free Credit
              </h2>

              <div className="mb-6">
                <input
                  type="number"
                  placeholder="Enter Amount"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  className="w-full px-4 py-4 border border-gray-300 rounded-xl text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent font-light text-lg"
                />
              </div>

              <button
                onClick={handleAddCredit}
                className="w-full bg-orange-500 text-white py-4 rounded-xl font-medium text-lg hover:bg-orange-600 transition-colors shadow-sm"
              >
                Add Now
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

// PaginationControls component
const PaginationControls = ({
  currentPage,
  itemsPerPage,
  organizations,
  filters,
  searchTerm,
  activeTab,
  onPageChange,
}: any) => {
  // Filtering logic (same as in table components)
  let filteredOrganizations = organizations;
  if (searchTerm) {
    filteredOrganizations = filteredOrganizations.filter((org: any) =>
      org.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
  if (filters) {
    if (filters.organization) {
      filteredOrganizations = filteredOrganizations.filter(
        (org: any) => org.id === filters.organization
      );
    }
    // Wallet/amount filter for Customer, amount for Expert
    if (activeTab === "Customer" && filters.walletRanges) {
      filteredOrganizations = filteredOrganizations.filter((org: any) => {
        const wallet = org.wallet || 0;
        let matchesWallet = false;
        if (filters.walletRanges.range1 && wallet >= 0 && wallet <= 100)
          matchesWallet = true;
        if (filters.walletRanges.range2 && wallet >= 101 && wallet <= 200)
          matchesWallet = true;
        if (filters.walletRanges.range3 && wallet >= 201 && wallet <= 300)
          matchesWallet = true;
        if (filters.walletRanges.range4 && wallet >= 301 && wallet <= 400)
          matchesWallet = true;
        if (filters.walletRanges.range5 && wallet >= 401 && wallet <= 500)
          matchesWallet = true;
        if (filters.walletRanges.range6 && wallet >= 501) matchesWallet = true;
        if (filters.walletRanges.custom) {
          const min = filters.walletRanges.customMin || 0;
          const max = filters.walletRanges.customMax || Number.MAX_SAFE_INTEGER;
          if (wallet >= min && wallet <= max) matchesWallet = true;
        }
        return matchesWallet;
      });
    }
    if (
      (activeTab === "Customer" && filters.amountRanges) ||
      (activeTab === "Expert" && filters.amountRanges)
    ) {
      filteredOrganizations = filteredOrganizations.filter((org: any) => {
        const amount =
          activeTab === "Customer" ? org.amount || 0 : org.totalEarnings || 0;
        let matchesAmount = false;
        if (filters.amountRanges.range1 && amount >= 0 && amount <= 100)
          matchesAmount = true;
        if (filters.amountRanges.range2 && amount >= 101 && amount <= 200)
          matchesAmount = true;
        if (filters.amountRanges.range3 && amount >= 201 && amount <= 300)
          matchesAmount = true;
        if (filters.amountRanges.range4 && amount >= 301 && amount <= 400)
          matchesAmount = true;
        if (filters.amountRanges.range5 && amount >= 401 && amount <= 500)
          matchesAmount = true;
        if (filters.amountRanges.range6 && amount >= 501) matchesAmount = true;
        if (filters.amountRanges.custom) {
          const min = filters.amountRanges.customMin || 0;
          const max = filters.amountRanges.customMax || Number.MAX_SAFE_INTEGER;
          if (amount >= min && amount <= max) matchesAmount = true;
        }
        return matchesAmount;
      });
    }
  }
  const totalPages = Math.ceil(filteredOrganizations.length / itemsPerPage);
  
  // Only show pagination if there are items and multiple pages (like in index.tsx)
  if (filteredOrganizations.length > 0 && totalPages > 1) {
    return (
      <div className="py-3 flex items-center justify-center">
        <nav className="flex space-x-1">
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index + 1}
              onClick={() => onPageChange(index + 1)}
              className={`px-2 py-1 text-[#696969] ${
                currentPage === index + 1
                  ? "bg-[#FAFAFA] border-[#EBEBEB] rounded-[8px] border"
                  : ""
              }`}
            >
              {String(index + 1).padStart(2, "0")}
            </button>
          ))}
        </nav>
      </div>
    );
  }
  
  return null;
};

// Customer Teams Table Component
const CustomerTeamsTable = ({
  organizations,
  searchTerm,
  filters,
  onEdit,
  onView,
  selectedId,
  currentPage,
  itemsPerPage,
}: {
  organizations: any[];
  searchTerm: string;
  filters: any;
  onEdit: (org: Organization) => void;
  onView: (org: Organization) => void;
  selectedId: string | null;
  currentPage: number;
  itemsPerPage: number;
}) => {
  const status = useSelector((state: RootState) => state.organization.status);
  const error = useSelector((state: RootState) => state.organization.error);
  const lastLoadedPlanType = useSelector((state: RootState) => state.organization.lastLoadedPlanType);
  const loading = status === "loading" && lastLoadedPlanType === "customer" && organizations.length === 0;
  // Filter organizations based on search term and filters
  let filteredOrganizations = organizations.filter((org: any) => {
    // Search filter
    const matchesSearch =
      (org.name || "").toLowerCase().includes(searchTerm?.toLowerCase());

    if (!filters) return matchesSearch;

    // Organization filter
    if (filters.organization && filters.organization !== org.id) {
      return false;
    }

    // Wallet range filter (only for Customer tab)
    if (filters.walletRanges) {
      const wallet = org.wallet || 0;
      let matchesWallet = false;

      if (filters.walletRanges.range1 && wallet >= 0 && wallet <= 100)
        matchesWallet = true;
      if (filters.walletRanges.range2 && wallet >= 101 && wallet <= 200)
        matchesWallet = true;
      if (filters.walletRanges.range3 && wallet >= 201 && wallet <= 300)
        matchesWallet = true;
      if (filters.walletRanges.range4 && wallet >= 301 && wallet <= 400)
        matchesWallet = true;
      if (filters.walletRanges.range5 && wallet >= 401 && wallet <= 500)
        matchesWallet = true;
      if (filters.walletRanges.range6 && wallet >= 501) matchesWallet = true;

      if (filters.walletRanges.custom) {
        const min = filters.walletRanges.customMin || 0;
        const max = filters.walletRanges.customMax || Number.MAX_SAFE_INTEGER;
        if (wallet >= min && wallet <= max) matchesWallet = true;
      }

      if (!matchesWallet) return false;
    }

    // Amount range filter (for Customer tab, use amount field)
    if (filters.amountRanges) {
      const amount = org.amount || 0;
      let matchesAmount = false;

      if (filters.amountRanges.range1 && amount >= 0 && amount <= 100)
        matchesAmount = true;
      if (filters.amountRanges.range2 && amount >= 101 && amount <= 200)
        matchesAmount = true;
      if (filters.amountRanges.range3 && amount >= 201 && amount <= 300)
        matchesAmount = true;
      if (filters.amountRanges.range4 && amount >= 301 && amount <= 400)
        matchesAmount = true;
      if (filters.amountRanges.range5 && amount >= 401 && amount <= 500)
        matchesAmount = true;
      if (filters.amountRanges.range6 && amount >= 501) matchesAmount = true;

      if (filters.amountRanges.custom) {
        const min = filters.amountRanges.customMin || 0;
        const max = filters.amountRanges.customMax || Number.MAX_SAFE_INTEGER;
        if (amount >= min && amount <= max) matchesAmount = true;
      }

      if (!matchesAmount) return false;
    }
    return matchesSearch;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredOrganizations.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const handleEdit = (organization: Organization) => {
    onEdit(organization);
  };

  const handleView = (organization: Organization) => {
    onView(organization);
  };

  const handleDelete = (organization: Organization) => {
    console.log("Delete organization:", organization);
  };

  if (loading) {
    return (
      <div className="min-w-full">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-[#FEF0EA]">
            <tr>
              <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                Organization Name
              </th>
              <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                Total Teams
              </th>
              <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                Total Members
              </th>
              <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                Total Sessions
              </th>
              <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                Last Activity
              </th>
              <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                Total Wallet
              </th>
              <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                Total Amount
              </th>
              <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {[...Array(5)].map((_, index) => (
              <tr key={index}>
                <td className="px-3 py-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                </td>
                <td className="px-3 py-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
                </td>
                <td className="px-3 py-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
                </td>
                <td className="px-3 py-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
                </td>
                <td className="px-3 py-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-20"></div>
                </td>
                <td className="px-3 py-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
                </td>
                <td className="px-3 py-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
                </td>
                <td className="px-3 py-3">
                  <div className="flex space-x-3">
                    <div className="h-5 w-5 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-5 w-5 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-10 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-[#FEF0EA]">
          <tr>
            <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
              Organization Name
            </th>
            <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
              Total Teams
            </th>
            <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
              Total Members
            </th>
            <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
              Total Sessions
            </th>
            <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
              Last Activity
            </th>
            <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
              Total Wallet
            </th>
            <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
              Total Amount
            </th>
            <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
              Action
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          <tr>
            <td
              colSpan={7}
              className="px-3 py-8 text-center text-[14px] nunito-regular text-red-500"
            >
              Error loading data: {error}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table className="min-w-full divide-y max-h-[700px] divide-gray-200">
      <thead className="bg-[#FEF0EA]">
        <tr>
          <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
            Organization Name
          </th>
          <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
            Total Teams
          </th>
          <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
            Total Members
          </th>
          <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
            Total Sessions
          </th>
          <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
            Last Activity
          </th>
          <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
            Total Wallet
          </th>
          <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
            Total Amount
          </th>
          <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
            Action
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {filteredOrganizations.length === 0 ? (
          <tr>
            <td colSpan={8} className="px-3 py-8 text-center">
              <div className="flex flex-col items-center py-20">
                <div className="w-24 h-24 mb-4 flex items-center justify-center">
                  <Search className="w-16 h-16 text-gray-400" />
                </div>
                <h2 className="text-[24px] nunito-semibold text-[#404040] mb-2">
                  Opps! No matching records found.
                </h2>
                <p className="text-[#919191] text-[14px] nunito-semibold mb-4 text-center">
                  Try changing the filters or search criteria to see more
                  results!
                </p>
              </div>
            </td>
          </tr>
        ) : (
          currentItems.map((org: Organization) => (
            <tr
              key={org.id}
              className={selectedId === org.id ? "bg-gray-100" : "bg-white"}
            >
              <td className="px-3 py-3 text-[14px] nunito-regular text-[#696969]">
                {org.name || "N/A"}
              </td>
              <td className="px-3 py-3 text-[14px] nunito-regular text-[#696969]">
                {org.totalTeams || 0}
              </td>
              <td className="px-3 py-3 text-[14px] nunito-regular text-[#696969]">
                {org.users ? org.users.length : 0}
              </td>{" "}
              <td className="px-3 py-3 text-[14px] nunito-regular text-[#696969]">
                {org.totalSessions || 0}
              </td>
              <td className="px-3 py-3 text-[14px] nunito-regular text-[#696969]">
                <span
                  className={
                    org.lastActivity !== "-" ? "text-gray-700" : "text-gray-400"
                  }
                >
                  {org.lastActivity || "-"}
                </span>
              </td>
              <td className="px-3 py-3 text-[14px] nunito-regular text-[#696969]">
                ${(org.wallet || 0).toFixed(2)}
              </td>{" "}
              <td className="px-3 py-3 text-[14px] nunito-regular text-[#696969]">
                ${(org.amount || 0).toFixed(2)}
                {status === "loading" && (
                  <span className="ml-2 text-xs text-gray-400">
                    calculating...
                  </span>
                )}
              </td>
              <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex space-x-3">
                  <button className="" onClick={() => handleEdit(org)}>
                    <Pencil className="h-[20px] w-[20px] text-[#919191] hover:text-gray-500 stroke-[2px]" />
                  </button>
                  <button className="" onClick={() => handleDelete(org)}>
                    <Trash className="h-[20px] w-[20px] text-[#919191] hover:text-gray-500 stroke-[2px]" />
                  </button>
                  <button
                    className="text-[#F15D18] underline cursor-pointer text-[14px] nunito-semibold"
                    onClick={() => handleView(org)}
                  >
                    View
                  </button>
                </div>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
};

// Expert Teams Table Component
const ExpertTeamsTable = ({
  organizations,
  searchTerm,
  filters,
  onEdit,
  onView,
  selectedId,
  currentPage,
  itemsPerPage,
}: {
  organizations: any[];
  searchTerm: string;
  filters: any;
  onEdit: (org: Organization) => void;
  onView: (org: Organization) => void;
  selectedId: string | null;
  currentPage: number;
  itemsPerPage: number;
}) => {
  const status = useSelector((state: RootState) => state.organization.status);
  const error = useSelector((state: RootState) => state.organization.error);
  const lastLoadedPlanType = useSelector((state: RootState) => state.organization.lastLoadedPlanType);
  const loading = status === "loading" && lastLoadedPlanType === "expert" && organizations.length === 0;
  // Filter organizations based on search term and filters
  let filteredOrganizations = organizations.filter((org: any) => {
    // Search filter
    const matchesSearch =
      (org.name || "").toLowerCase().includes(searchTerm.toLowerCase());

    if (!filters) return matchesSearch;

    // Organization filter
    if (filters.organization && filters.organization !== org.id) {
      return false;
    }

    // For Expert tab, no wallet filter is shown (only organization and total earnings)

    // Amount range filter (for expert teams, use totalEarnings for "Total Earnings" filter)
    if (filters.amountRanges) {
      const amount = org.totalEarnings || 0;
      let matchesAmount = false;

      if (filters.amountRanges.range1 && amount >= 0 && amount <= 100)
        matchesAmount = true;
      if (filters.amountRanges.range2 && amount >= 101 && amount <= 200)
        matchesAmount = true;
      if (filters.amountRanges.range3 && amount >= 201 && amount <= 300)
        matchesAmount = true;
      if (filters.amountRanges.range4 && amount >= 301 && amount <= 400)
        matchesAmount = true;
      if (filters.amountRanges.range5 && amount >= 401 && amount <= 500)
        matchesAmount = true;
      if (filters.amountRanges.range6 && amount >= 501) matchesAmount = true;

      if (filters.amountRanges.custom) {
        const min = filters.amountRanges.customMin || 0;
        const max = filters.amountRanges.customMax || Number.MAX_SAFE_INTEGER;
        if (amount >= min && amount <= max) matchesAmount = true;
      }

      if (!matchesAmount) return false;
    }
    return matchesSearch;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredOrganizations.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const handleEdit = (organization: Organization) => {
    onEdit(organization);
  };

  const handleView = (organization: Organization) => {
    onView(organization);
  };

  const handleDelete = (organization: Organization) => {
    console.log("Delete organization:", organization);
  };
  if (loading) {
    return (
      <div className="min-w-full">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-[#FEF0EA]">
            <tr>
              <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                Organization Name
              </th>
              <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                Total Teams
              </th>
              <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                Total Members
              </th>
              <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                Total Sessions
              </th>
              <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                Last Activity
              </th>
              <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                Total Earnings
              </th>
              <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {[...Array(5)].map((_, index) => (
              <tr key={index}>
                <td className="px-3 py-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                </td>
                <td className="px-3 py-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
                </td>
                <td className="px-3 py-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
                </td>
                <td className="px-3 py-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
                </td>
                <td className="px-3 py-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-20"></div>
                </td>
                <td className="px-3 py-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
                </td>
                <td className="px-3 py-3">
                  <div className="flex space-x-3">
                    <div className="h-5 w-5 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-5 w-5 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-10 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-[#FEF0EA]">
          <tr>
            <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
              Organization Name
            </th>
            <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
              Total Teams
            </th>
            <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
              Total Members
            </th>
            <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
              Total Sessions
            </th>
            <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
              Last Activity
            </th>
            <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
              Total Earnings
            </th>
            <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
              Action
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          <tr>
            <td
              colSpan={6}
              className="px-3 py-8 text-center text-[14px] nunito-regular text-red-500"
            >
              Error loading data: {error}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table className="min-w-full divide-y max-h-[700px] divide-gray-200">
      <thead className="bg-[#FEF0EA]">
        <tr>
          <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
            Organization Name
          </th>
          <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
            Total Teams
          </th>
          <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
            Total Members
          </th>
          <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
            Total Sessions
          </th>
          <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
            Last Activity
          </th>
          <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
            Total Earnings
          </th>
          <th className="px-3 py-3 text-left text-[14px] nunito-semibold text-[#404040] tracking-wider">
            Action
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {filteredOrganizations.length === 0 ? (
          <tr>
            <td colSpan={7} className="px-3 py-8 text-center">
              <div className="flex flex-col items-center py-20">
                <div className="w-24 h-24 mb-4 flex items-center justify-center">
                  <Search className="w-16 h-16 text-gray-400" />
                </div>
                <h2 className="text-[24px] nunito-semibold text-[#404040] mb-2">
                  Opps! No matching records found.
                </h2>
                <p className="text-[#919191] text-[14px] nunito-semibold mb-4 text-center">
                  Try changing the filters or search criteria to see more
                  results!
                </p>
              </div>
            </td>
          </tr>
        ) : (
          currentItems.map((org: Organization) => (
            <tr
              key={org.id}
              className={selectedId === org.id ? "bg-gray-100" : "bg-white"}
            >
              <td className="px-3 py-3 text-[14px] nunito-regular text-[#696969]">
                {org.name || "N/A"}
              </td>
              <td className="px-3 py-3 text-[14px] nunito-regular text-[#696969]">
                {org.totalTeams || 0}
              </td>
              <td className="px-3 py-3 text-[14px] nunito-regular text-[#696969]">
                {org.users ? org.users.length : 0}
              </td>{" "}
              <td className="px-3 py-3 text-[14px] nunito-regular text-[#696969]">
                {org.totalSessions || 0}
              </td>
              <td className="px-3 py-3 text-[14px] nunito-regular text-[#696969]">
                <span
                  className={
                    org.lastActivity !== "-" ? "text-gray-700" : "text-gray-400"
                  }
                >
                  {org.lastActivity || "-"}
                </span>
              </td>
              <td className="px-3 py-3 text-[14px] nunito-regular text-[#696969]">
                ${(org.totalEarnings || 0).toFixed(2)}
                {status === "loading" && (
                  <span className="ml-2 text-xs text-gray-400">
                    calculating...
                  </span>
                )}
              </td>
              <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex space-x-3">
                  <button className="" onClick={() => handleEdit(org)}>
                    <Pencil className="h-[20px] w-[20px] text-[#919191] hover:text-gray-500 stroke-[2px]" />
                  </button>
                  <button className="" onClick={() => handleDelete(org)}>
                    <Trash className="h-[20px] w-[20px] text-[#919191] hover:text-gray-500 stroke-[2px]" />
                  </button>
                  <button
                    className="text-[#F15D18] underline cursor-pointer text-[14px] nunito-semibold"
                    onClick={() => handleView(org)}
                  >
                    View
                  </button>
                </div>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
};

export default AdminTeams;
